import { type ReactNode, useMemo } from 'react';
import { Fade } from '@/components/ui/fade';
import { useMinLoading } from '@/hooks/use-min-loading';
import { useCreatorDistribution } from '@/hooks/use-creator-distribution';
import type { CreatorDistribution } from '@/types/progress';
import { categorySlices, sexSlices } from '../utils/distribution-slices';
import { followerSlices } from '../utils/distribution-followers';
import { DistributionDonut } from './distribution-donut';
import { DistributionSkeleton } from './distribution-skeleton';

const GRID = 'grid grid-cols-1 gap-lg sm:grid-cols-2 lg:grid-cols-3';

function DistributionDonuts({
  data,
}: {
  data: CreatorDistribution;
}): ReactNode {
  const donuts = useMemo(
    () => [
      { title: 'Categoría', slices: categorySlices(data.category) },
      { title: 'Sexo', slices: sexSlices(data.sex) },
      { title: 'Seguidores', slices: followerSlices(data.followers) },
    ],
    [data]
  );
  return (
    <div className={GRID}>
      {donuts.map((d) => (
        <DistributionDonut key={d.title} title={d.title} slices={d.slices} />
      ))}
    </div>
  );
}

export function CreatorsDistributionSection(): ReactNode {
  const { data, isLoading } = useCreatorDistribution();
  const showSkeleton = useMinLoading(isLoading);
  const state = showSkeleton || !data ? 'loading' : 'data';
  // Nothing collected yet: three empty rings say less than no section.
  if (state === 'data' && data && data.total === 0) return null;
  return (
    <Fade activeKey={state}>
      {state === 'loading' ? (
        <DistributionSkeleton />
      ) : (
        <DistributionDonuts data={data!} />
      )}
    </Fade>
  );
}
